<template>
  <div
    v-if="visible"
    class="chat-window-overlay"
    @click="handleOverlayClick"
  >
    <div
      class="chat-window"
      @click.stop
    >
      <!-- Chat Header -->
      <div class="chat-header">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
            <img
              :src="symphonyLogo"
              alt="Symphony Bot"
              class="w-full h-full object-contain"
            >
          </div>
          <div>
            <h3 class="font-semibold text-gray-800">
              Symphony Bot
            </h3>
            <p class="text-xs text-gray-500">
              AI Assistant
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button 
            v-tooltip="'Minimize'" 
            icon="pi pi-minus" 
            text 
            size="small"
            @click="minimizeChat"
          />
          <Button 
            v-tooltip="'Close'" 
            icon="pi pi-times" 
            text 
            size="small"
            @click="closeChat"
          />
        </div>
      </div>

      <!-- Chat Messages -->
      <div
        ref="messagesContainer"
        class="chat-messages"
      >
        <div
          v-if="messages.length === 0"
          class="empty-state"
        >
          <i class="pi pi-comments text-4xl text-gray-300 mb-4" />
          <p class="text-gray-500">
            Start a conversation with Symphony Bot
          </p>
        </div>
                
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="message-wrapper"
        >
          <div
            :class="[
              'message',
              message.type === 'user' ? 'user-message' : 'ai-message'
            ]"
          >
            <div class="message-avatar">
              <img
                v-if="message.type === 'ai'"
                :src="symphonyLogo"
                alt="Symphony Bot"
                class="w-full h-full object-contain"
              >
              <i
                v-else
                class="pi pi-user"
              />
            </div>
            <div class="message-content">
              <div
                class="message-text"
                v-html="formatMessage(message.text)"
              />
              <div class="message-time">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Typing indicator -->
        <div
          v-if="isTyping"
          class="message-wrapper"
        >
          <div class="message ai-message">
            <div class="message-avatar">
              <img
                :src="symphonyLogo"
                alt="Symphony Bot"
                class="w-full h-full object-contain"
              >
            </div>
            <div class="message-content">
              <div class="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="chat-input">
        <div class="input-container">
          <Textarea
            v-model="inputMessage"
            placeholder="Type your message..."
            :rows="1"
            :auto-resize="true"
            class="message-input"
            :disabled="isLoading"
            @keydown.enter.prevent="handleSendMessage"
          />
          <Button
            icon="pi pi-send"
            :disabled="!inputMessage.trim() || isLoading"
            class="send-button"
            size="small"
            @click="handleSendMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, defineProps, defineEmits } from 'vue';
import { AIChatService } from '@/services/aiChatService'
import SymphonyLogo from '@/assets/logos/Symphony_Logo_Only.png';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'minimize', 'update:visible']);

// Reactive state
const inputMessage = ref('');
const messages = ref([]);
const isLoading = ref(false);
const isTyping = ref(false);
const messagesContainer = ref(null);
const symphonyLogo = SymphonyLogo;

// Sample messages for demonstration
const sampleMessages = [
    {
        type: 'ai',
        text: 'Hello! I\'m Symphony Bot, your AI assistant. How can I help you today?',
        timestamp: new Date()
    }
];

// Initialize with sample message
if (messages.value.length === 0) {
    messages.value = [...sampleMessages];
}

// Methods
const handleSendMessage = async () => {
    if (!inputMessage.value.trim() || isLoading.value) return;

    const userMessage = {
        type: 'user',
        text: inputMessage.value.trim(),
        timestamp: new Date()
    };

    messages.value.push(userMessage);
    const currentInput = inputMessage.value;
    inputMessage.value = '';
    
    // Scroll to bottom
    await nextTick();
    scrollToBottom();

    // Show typing indicator
    isTyping.value = true;
    isLoading.value = true;

    try {
        // Simulate AI response (replace with actual API call)
        await simulateAIResponse(currentInput);
    } catch (error) {
        console.error('Error sending message:', error);
        messages.value.push({
            type: 'ai',
            text: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date()
        });
    } finally {
        isTyping.value = false;
        isLoading.value = false;
        await nextTick();
        scrollToBottom();
    }
};

const simulateAIResponse = async (userInput) => {
    // Call the real support API endpoint
    try {
        const response = await AIChatService.sendMessage(userInput);

        const answer = response?.answer ?? response?.message;
        if (!answer) {
            throw new Error('No answer received from support service');
        }
        
        messages.value.push({
            type: 'ai',
            text: answer,
            timestamp: new Date()
        });
    } catch (error) {
        console.error('[ChatWindow] Error sending message:', error);
        
        // Handle different error types
        let errorMessage = 'Sorry, I encountered an error. Please try again.';
        
        if (error.response?.status === 422) {
            errorMessage = 'Please enter a valid message (max 4000 characters).';
        } else if (error.response?.status === 401) {
            errorMessage = 'Please log in to use support chat.';
        } else if (error.response?.status >= 500) {
            errorMessage = 'Support service unavailable. Please try again later.';
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (!error.response) {
            errorMessage = 'Connection error. Please check your internet and try again.';
        }
        
        messages.value.push({
            type: 'ai',
            text: `Sorry, I encountered an error: ${errorMessage}`,
            timestamp: new Date()
        });
    }
};

const scrollToBottom = () => {
    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
};

const formatMessage = (text) => {
    // Convert line breaks to HTML
    return text.replace(/\n/g, '<br>');
};

const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const handleOverlayClick = () => {
    closeChat();
};

const closeChat = () => {
    emit('close');
    emit('update:visible', false);
};

const minimizeChat = () => {
    emit('minimize');
};

// Watch for new messages to auto-scroll
watch(messages, () => {
    nextTick(() => {
        scrollToBottom();
    });
}, { deep: true });

// Auto-scroll when chat opens
watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        nextTick(() => {
            scrollToBottom();
        });
    }
});
</script>

<style scoped>
.chat-window-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.chat-window {
    width: 90vw;
    max-width: 500px;
    height: 80vh;
    max-height: 600px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.chat-header {
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f9fafb;
}

.chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
}

.message-wrapper {
    display: flex;
    flex-direction: column;
}

.message {
    display: flex;
    gap: 8px;
    max-width: 80%;
}

.user-message {
    align-self: flex-end;
    flex-direction: row-reverse;
}

.ai-message {
    align-self: flex-start;
}

.message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}

.user-message .message-avatar {
    background: #3b82f6;
    color: white;
}

.ai-message .message-avatar {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.message-content {
    flex: 1;
    min-width: 0;
}

.message-text {
    background: #f3f4f6;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.4;
    word-wrap: break-word;
}

.user-message .message-text {
    background: #3b82f6;
    color: white;
}

.message-time {
    font-size: 11px;
    color: #9ca3af;
    margin-top: 4px;
    text-align: right;
}

.ai-message .message-time {
    text-align: left;
}

.typing-indicator {
    display: flex;
    gap: 4px;
    padding: 8px 12px;
    background: #f3f4f6;
    border-radius: 12px;
}

.typing-indicator span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.5;
    }
    30% {
        transform: translateY(-10px);
        opacity: 1;
    }
}

.chat-input {
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

.input-container {
    display: flex;
    gap: 8px;
    align-items: flex-end;
}

.message-input {
    flex: 1;
    resize: none;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    line-height: 1.4;
}

.message-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.send-button {
    flex-shrink: 0;
}

/* Scrollbar styling */
.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>
